import React, { useEffect } from 'react';
import { ArrowRight, Check, ChevronDown, Users, Clock, Zap, Heart, Briefcase, Eye, Smartphone, Star, Instagram } from 'lucide-react';
import FloatingChatButton from '../components/FloatingChatButton';

const DWAPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    "Start earning with done-for-you products",
    "Create digital products that sell while you sleep",
    "Build income streams through affiliate partnerships",
    "Master organic marketing that actually converts"
  ];

  const modules = [
    { title: "Foundation & Mindset", items: ["Platform navigation guide", "Confidence building", "Digital marketing basics"] },
    { title: "Systems & Tech", items: ["Funnel setup", "Systeme.io mastery", "Email marketing", "Pre-launch strategy"] },
    { title: "Marketing Mastery", items: ["Content creation", "Viral reel system", "Attraction marketing", "Instagram growth"] },
  ];

  const whatMakesDifferent = [
    { icon: Clock, title: "True Time Freedom", description: "Build around your schedule, not the other way around." },
    { icon: Users, title: "130K+ Community", description: "Daily inspiration, support, and accountability." },
    { icon: Zap, title: "MRR Included", description: "Master Resell Rights — resell and keep 100% profit." },
    { icon: Star, title: "Lifetime Access", description: "Pay once, access forever. All future updates included." }
  ];

  const testimonials = [
    { name: "Sarah M.", result: "Replaced 9-5 income in 3 months" },
    { name: "Jessica T.", result: "First $5K month" },
    { name: "Amanda K.", result: "Built freedom-based business" },
    { name: "Michelle R.", result: "10K+ engaged followers" }
  ];

  const faqs = [
    { q: "What exactly is Digital Wealth Academy?", a: "DWA is a comprehensive digital marketing course teaching you to build multiple income streams through digital products, affiliate marketing, and content creation." },
    { q: "Do I have to sell DWA to make money?", a: "Absolutely not! While you can promote DWA as an affiliate, you'll learn strategies that apply to ANY digital product or business." },
    { q: "I'm not tech-savvy. Can I do this?", a: "Yes! The course is designed for complete beginners with step-by-step tutorials. If you can use a smartphone, you can do this." },
    { q: "Are there any hidden fees?", a: "No hidden fees. One payment gives you lifetime access to everything." }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f6f3' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 py-6 px-8" style={{ backgroundColor: 'rgba(31, 45, 36, 0.98)', backdropFilter: 'blur(10px)' }}>
        <div className="container mx-auto flex items-center justify-between">
          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60" style={{ color: 'rgba(255,255,255,0.8)' }}>HOME</a>
            <a href="#about" className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60" style={{ color: 'rgba(255,255,255,0.8)' }}>ABOUT DWA</a>
            <a href="#pricing" className="text-xs tracking-[0.15em] font-light transition-colors hover:opacity-60" style={{ color: 'rgba(255,255,255,0.8)' }}>PRICING</a>
          </div>
          <a href="/" className="text-sm tracking-[0.3em] font-light hover:opacity-70 transition-opacity italic" style={{ color: '#ffffff', fontFamily: "'Playfair Display', serif" }}>Farzeen</a>
          <a href="https://instagram.com/itsfarzeenkhan" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-60" style={{ color: '#ffffff' }}>
            <Instagram size={18} strokeWidth={1.5} />
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative" style={{ backgroundColor: '#1F2D24' }}>
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.03) 0%, transparent 70%)' }} />
        
        <div className="container mx-auto px-8 py-32 relative z-10 text-center">
          <p className="text-xs tracking-[0.4em] mb-8 font-light uppercase" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Your Complete Blueprint
          </p>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extralight tracking-[0.1em] leading-[1.4] mb-8" style={{ color: '#ffffff', fontFamily: "'Playfair Display', serif" }}>
            Digital Wealth
            <br />
            <span className="italic font-light" style={{ color: '#d4b896' }}>Academy</span>
          </h1>
          
          <p className="text-base font-light leading-relaxed mb-12 max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Join 130,000+ students building freedom-based businesses through digital marketing. Your one-stop shop for online income.
          </p>
          
          <a 
            href="https://stan.store/FarzeenDigitalWealth/p/digital-wealth-academy--1sj4lwda" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center text-xs tracking-[0.2em] px-10 py-4 transition-all duration-300 hover:bg-white hover:text-[#1F2D24] border"
            style={{ backgroundColor: 'transparent', color: '#ffffff', borderColor: 'rgba(255,255,255,0.3)' }}
          >
            I'M READY
          </a>
          
          <p className="text-xs tracking-[0.1em] mt-4 font-light italic" style={{ color: 'rgba(255,255,255,0.4)' }}>
            One payment. Lifetime access.
          </p>
        </div>
        
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <ChevronDown size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32" style={{ backgroundColor: '#f8f6f3' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs tracking-[0.3em] mb-6 font-light text-center" style={{ color: '#1F2D24' }}>
              WHAT YOU'LL LEARN
            </p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.1em] leading-relaxed mb-16 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}>
              Turn your phone into a business
              <br />
              <span className="italic" style={{ color: '#1F2D24' }}>that gives you freedom</span>
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 p-6" style={{ backgroundColor: '#1F2D24' }}>
                  <Check size={18} style={{ color: '#d4b896' }} className="flex-shrink-0 mt-1" />
                  <p className="text-sm font-light" style={{ color: 'rgba(255,255,255,0.9)' }}>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32" style={{ backgroundColor: '#1F2D24' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-xs tracking-[0.3em] mb-6 font-light" style={{ color: '#d4b896' }}>THE STORY</p>
                <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-8" style={{ fontFamily: "'Playfair Display', serif", color: '#ffffff' }}>
                  Can someone like me
                  <br />
                  <span className="italic" style={{ color: '#d4b896' }}>really do this?</span>
                </h2>
                <p className="text-base font-light leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  I asked myself this same question not too long ago. I was burnt out, overwhelmed, and searching for something more.
                </p>
                <p className="text-base font-light leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  That's why Digital Wealth Academy exists — to give you everything you need in one place. No more piecing together random courses. No more confusion.
                </p>
              </div>
              <div className="text-center">
                <div className="text-6xl md:text-7xl font-extralight mb-4" style={{ color: '#d4b896', fontFamily: "'Playfair Display', serif" }}>130K+</div>
                <p className="text-xs tracking-[0.2em] font-light" style={{ color: 'rgba(255,255,255,0.6)' }}>STUDENTS & GROWING</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-32" style={{ backgroundColor: '#f8f6f3' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs tracking-[0.3em] mb-6 font-light text-center" style={{ color: '#1F2D24' }}>CURRICULUM</p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-16 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}>
              Everything you need,
              <span className="italic" style={{ color: '#1F2D24' }}> all in one place</span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {modules.map((module, index) => (
                <div key={index} className="p-8 border" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                  <h3 className="text-sm tracking-[0.15em] font-light mb-6" style={{ color: '#1F2D24' }}>{module.title.toUpperCase()}</h3>
                  <ul className="space-y-3">
                    {module.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span style={{ color: '#1F2D24' }}>—</span>
                        <span className="text-sm font-light" style={{ color: '#666' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Different */}
      <section className="py-32" style={{ backgroundColor: '#1F2D24' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs tracking-[0.3em] mb-6 font-light text-center" style={{ color: '#d4b896' }}>WHY DWA</p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-16 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#ffffff' }}>
              What makes this
              <span className="italic" style={{ color: '#d4b896' }}> different</span>
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whatMakesDifferent.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center" style={{ border: '1px solid rgba(255,255,255,0.2)' }}>
                      <Icon size={20} style={{ color: '#d4b896' }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xs tracking-[0.15em] font-light mb-2" style={{ color: '#ffffff' }}>{item.title.toUpperCase()}</h3>
                    <p className="text-sm font-light" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32" style={{ backgroundColor: '#f8f6f3' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs tracking-[0.3em] mb-6 font-light text-center" style={{ color: '#1F2D24' }}>SUCCESS STORIES</p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-16 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}>
              Real results from
              <span className="italic" style={{ color: '#1F2D24' }}> real students</span>
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="p-6 text-center" style={{ backgroundColor: '#ffffff' }}>
                  <div className="flex justify-center gap-1 mb-4">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} size={12} fill="#1F2D24" stroke="none" />
                    ))}
                  </div>
                  <p className="text-sm font-medium mb-2" style={{ color: '#1a1a1a' }}>{testimonial.name}</p>
                  <p className="text-xs font-light" style={{ color: '#666' }}>{testimonial.result}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-32" style={{ backgroundColor: '#1F2D24' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-lg mx-auto text-center">
            <p className="text-xs tracking-[0.3em] mb-6 font-light" style={{ color: '#d4b896' }}>INVESTMENT</p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-8" style={{ fontFamily: "'Playfair Display', serif", color: '#ffffff' }}>
              Join Digital Wealth
              <span className="italic" style={{ color: '#d4b896' }}> Academy</span>
            </h2>
            
            <div className="p-10 mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="text-4xl font-extralight mb-2" style={{ color: '#ffffff', fontFamily: "'Playfair Display', serif" }}>$497</div>
              <p className="text-xs tracking-[0.15em] font-light mb-8" style={{ color: 'rgba(255,255,255,0.5)' }}>ONE-TIME PAYMENT</p>
              
              <ul className="space-y-3 text-left mb-10">
                {["Complete DWA course access", "MRR (Master Resell Rights)", "Private community access", "Content vault & marketing materials", "Lifetime updates & new modules"].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check size={14} style={{ color: '#d4b896' }} />
                    <span className="text-sm font-light" style={{ color: 'rgba(255,255,255,0.8)' }}>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <a 
                href="https://stan.store/FarzeenDigitalWealth/p/digital-wealth-academy--1sj4lwda" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full py-4 text-xs tracking-[0.2em] font-light transition-all hover:opacity-80"
                style={{ backgroundColor: '#d4b896', color: '#1F2D24' }}
              >
                I'M READY
              </a>
            </div>
            
            <p className="text-xs font-light" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Payment plan available at checkout
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32" style={{ backgroundColor: '#f8f6f3' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.3em] mb-6 font-light text-center" style={{ color: '#1F2D24' }}>QUESTIONS</p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-16 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#1a1a1a' }}>
              Frequently
              <span className="italic" style={{ color: '#1F2D24' }}> asked</span>
            </h2>
            
            <div className="space-y-8">
              {faqs.map((faq, index) => (
                <div key={index} className="pb-8" style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                  <h3 className="text-sm font-medium mb-3" style={{ color: '#1a1a1a' }}>{faq.q}</h3>
                  <p className="text-sm font-light" style={{ color: '#666' }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32" style={{ backgroundColor: '#1F2D24' }}>
        <div className="container mx-auto px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-extralight tracking-[0.05em] leading-relaxed mb-8" style={{ fontFamily: "'Playfair Display', serif", color: '#ffffff' }}>
              Ready to build your
              <br />
              <span className="italic" style={{ color: '#d4b896' }}>freedom-based business?</span>
            </h2>
            
            <a 
              href="https://stan.store/FarzeenDigitalWealth/p/digital-wealth-academy--1sj4lwda" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-xs tracking-[0.2em] px-10 py-4 transition-all duration-300 hover:opacity-80"
              style={{ backgroundColor: '#d4b896', color: '#1F2D24' }}
            >
              JOIN 130,000+ STUDENTS
              <ArrowRight size={16} strokeWidth={1.5} />
            </a>
            
            <p className="mt-8 text-xs font-light" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Questions? DM me <a href="https://instagram.com/itsfarzeenkhan" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#d4b896' }}>@itsfarzeenkhan</a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="container mx-auto px-8 text-center">
          <p className="text-xs font-light" style={{ color: 'rgba(255,255,255,0.3)' }}>
            © {new Date().getFullYear()} Farzeen Digital Wealth. All rights reserved.
          </p>
        </div>
      </footer>

      <FloatingChatButton />
    </div>
  );
};

export default DWAPage;
